import React, { useEffect, useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  getMoonZodiacSign,
  getMoonPhaseData,
  isMoonAscending,
  getMonthNodeDays,
  getMonthApsides,
  getMonthMoonQuarters,
  ZODIAC_SYMBOLS,
} from '@/lib/astronomy';
import {
  ascendingInHemisphere,
  seasonalMonth,
  otherHemisphere,
  monthSlug,
  moonPath,
  moonIndexPath,
  HEMISPHERE_LABEL,
} from '@/lib/hemisphere';
import type { Hemisphere } from '@/lib/hemisphere';
import { useHemisphere } from '@/context/HemisphereContext';
import { ZODIAC_TO_DAY_TYPE, DAY_TYPE_META, MOON_PHASE_EMOJI } from '@/types/biodynamic';
import type { BiodynamicDayType, MoonPhase, ZodiacSign } from '@/types/biodynamic';
import { PLANTS } from '@/data/plants';
import PageHeader from '@/components/ui/PageHeader';
import { usePageMeta } from '@/hooks/usePageMeta';
import './MoonPlantingPage.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const PHASE_LABEL: Record<MoonPhase, string> = {
  'new': 'New moon',
  'waxing-crescent': 'Waxing crescent',
  'first-quarter': 'First quarter',
  'waxing-gibbous': 'Waxing gibbous',
  'full': 'Full moon',
  'waning-gibbous': 'Waning gibbous',
  'last-quarter': 'Last quarter',
  'waning-crescent': 'Waning crescent',
};

// Bounded deliberately. The calendar can be computed for any date, but an
// unbounded URL space would let crawlers wander through thousands of thin
// pages. These limits stay fixed so a page that ranks never starts 404ing.
const MIN_YEAR = 2020;
const MAX_YEAR = 2035;

/** Parses "august-2026" into its month index and year, or null if unusable. */
function parseMonthSlug(slug: string | undefined): { year: number; month: number } | null {
  if (!slug) return null;
  const match = /^([a-z]+)-(\d{4})$/.exec(slug.toLowerCase());
  if (!match) return null;

  const month = MONTH_NAMES.findIndex(m => m.toLowerCase() === match[1]);
  const year = Number(match[2]);
  if (month === -1 || year < MIN_YEAR || year > MAX_YEAR) return null;

  return { year, month };
}

interface CalendarDay {
  day: number;
  weekday: string;
  dayType: BiodynamicDayType;
  sign: ZodiacSign;
  phase: MoonPhase;
  illumination: number;
  ascending: boolean;
  avoid: boolean;
  avoidReason: string | null;
}

function buildMonth(year: number, month: number, hemisphere: Hemisphere): CalendarDay[] {
  // Node and apsis days are a property of the month, so compute them once
  // rather than per day.
  const nodeDays = getMonthNodeDays(year, month);
  const apsides = getMonthApsides(year, month);
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  return Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    // Midday avoids the sign flipping either side of a timezone boundary.
    const date = new Date(year, month, day, 12);
    const sign = getMoonZodiacSign(date);
    const { phase, illumination } = getMoonPhaseData(date);

    const node = nodeDays.find(n => n.day === day);
    const apsis = apsides.find(a => a.day === day);
    const perigee = apsis?.type === 'perigee';

    return {
      day,
      weekday: WEEKDAYS[date.getDay()],
      // The zodiac sign — and so the day type — is the same worldwide.
      dayType: ZODIAC_TO_DAY_TYPE[sign],
      sign,
      phase,
      illumination,
      ascending: ascendingInHemisphere(isMoonAscending(date), hemisphere),
      avoid: !!node || perigee,
      avoidReason: node ? 'Lunar node — avoid sowing' : perigee ? 'Perigee — avoid sowing' : null,
    };
  });
}

/** Two-way switch that navigates to the same month in the other hemisphere. */
function HemisphereToggle({ current, to }: { current: Hemisphere; to: string }) {
  const { setHemisphere } = useHemisphere();
  const other = otherHemisphere(current);
  return (
    <div className="hemisphere-toggle" role="group" aria-label="Hemisphere">
      <span className="hemisphere-toggle-current" aria-current="true">
        {current === 'northern' ? '🌍' : '🌏'} {HEMISPHERE_LABEL[current]}
      </span>
      <Link
        to={to}
        className="hemisphere-toggle-switch"
        onClick={() => setHemisphere(other)}
      >
        Switch to {other}
      </Link>
    </div>
  );
}

export function MoonPlantingIndexPage({ hemisphere = 'northern' as Hemisphere }: { hemisphere?: Hemisphere }) {
  const { hemisphere: preferred } = useHemisphere();

  usePageMeta(
    hemisphere === 'southern'
      ? 'Southern Hemisphere Moon Planting Calendar'
      : 'Moon Planting Calendar',
    hemisphere === 'southern'
      ? 'Month-by-month moon planting calendars for the southern hemisphere — root, leaf, flower and fruit days, moon phases and southern sowing seasons.'
      : 'Month-by-month moon planting calendars showing root, leaf, flower and fruit days, moon phases and the best sowing dates for every month.',
  );

  const now = new Date();
  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  return (
    <div className="moon-planting-page">
      <PageHeader
        title={hemisphere === 'southern' ? 'Southern Hemisphere Moon Planting' : 'Moon Planting Calendar'}
        subtitle="Sow, transplant and harvest in step with the moon. Each month shows every day's type, phase and the dates worth avoiding."
        eyebrow="Plant with the moon"
        decorationEmoji="🌕"
      />

      <div className="container section">
        <HemisphereToggle current={hemisphere} to={moonIndexPath(otherHemisphere(hemisphere))} />

        {preferred !== hemisphere && (
          <p className="hemisphere-hint">
            {preferred === 'southern' ? '🌏' : '🌍'} Your time zone looks like the{' '}
            {preferred} hemisphere.{' '}
            <Link to={moonIndexPath(preferred)}>
              View the {preferred} calendar
            </Link>
            .
          </p>
        )}

        <div className="moon-month-grid">
          {months.map(({ year, month }, i) => (
            <Link
              key={monthSlug(year, month)}
              to={moonPath(year, month, hemisphere)}
              className={`moon-month-card animate-fade-in-delay-${Math.min(i + 1, 5)}`}
            >
              <span className="moon-month-card-name text-title">{MONTH_NAMES[month]}</span>
              <span className="text-caption" style={{ color: 'var(--ink-faint)' }}>{year}</span>
            </Link>
          ))}
        </div>

        <p className="text-body moon-index-note">
          New to this? The{' '}
          <Link to="/biodynamic">biodynamic growing guide</Link>{' '}
          explains what root, leaf, flower and fruit days mean and where the practice comes from.
        </p>
      </div>
    </div>
  );
}

export default function MoonPlantingPage({ hemisphere = 'northern' as Hemisphere }: { hemisphere?: Hemisphere }) {
  const { slug } = useParams<{ slug: string }>();
  const parsed = parseMonthSlug(slug);
  const { hemisphere: sitePreference, setHemisphere: setSitePreference } = useHemisphere();

  // Someone arriving here from a search lands on a hemisphere-specific URL.
  // Adopt it site-wide so clicking through to a plant page doesn't silently
  // switch them back to the other hemisphere's seasons.
  useEffect(() => {
    if (sitePreference !== hemisphere) setSitePreference(hemisphere);
  }, [hemisphere, sitePreference, setSitePreference]);

  const monthName = parsed ? MONTH_NAMES[parsed.month] : '';
  const south = hemisphere === 'southern';

  const days = useMemo(
    () => (parsed ? buildMonth(parsed.year, parsed.month, hemisphere) : []),
    [parsed?.year, parsed?.month, hemisphere],
  );

  const counts = useMemo(() => {
    const c: Record<BiodynamicDayType, number> = { root: 0, leaf: 0, flower: 0, fruit: 0 };
    days.forEach(d => { c[d.dayType] += 1; });
    return c;
  }, [days]);

  // Taken from the exact event times, not from the day-by-day classification,
  // so the stated dates are right even when an event falls near midnight.
  const quarters = useMemo(
    () => (parsed ? getMonthMoonQuarters(parsed.year, parsed.month) : []),
    [parsed?.year, parsed?.month],
  );
  const fullMoon = quarters.find(q => q.quarter === 'full');
  const newMoon = quarters.find(q => q.quarter === 'new');
  const bestSowing = days.filter(d => d.ascending && !d.avoid);

  usePageMeta(
    parsed
      ? south
        ? `Southern Hemisphere Moon Planting Calendar for ${monthName} ${parsed.year}`
        : `Moon Planting Calendar for ${monthName} ${parsed.year}`
      : 'Moon Planting Calendar',
    parsed
      ? `Moon planting calendar for ${monthName} ${parsed.year}${south ? ', southern hemisphere' : ''} — every day's root, leaf, flower or fruit type, moon phase, and the best dates to sow and transplant.`
      : 'Moon planting calendars month by month.',
    parsed
      ? `/og/moon-${south ? 'southern-' : ''}${monthSlug(parsed.year, parsed.month)}.png`
      : undefined,
  );

  if (!parsed) return <Navigate to={moonIndexPath(hemisphere)} replace />;

  const { year, month } = parsed;
  const prev = new Date(year, month - 1, 1);
  const next = new Date(year, month + 1, 1);

  // The plant database is written for a northern grower, so a southern reader
  // looking at August needs February's sowings.
  const sowingMonth = seasonalMonth(month + 1, hemisphere);
  const sowThisMonth = PLANTS.filter(
    p => p.calendar.sowIndoors?.includes(sowingMonth) || p.calendar.sowOutdoors?.includes(sowingMonth),
  );
  const harvestThisMonth = PLANTS.filter(p => p.calendar.harvest?.includes(sowingMonth));

  const dayTypeOrder: BiodynamicDayType[] = ['root', 'leaf', 'flower', 'fruit'];

  return (
    <div className="moon-planting-page">
      <PageHeader
        title={`${monthName} ${year}`}
        subtitle={`Moon planting calendar — ${counts.root} root days, ${counts.leaf} leaf days, ${counts.flower} flower days and ${counts.fruit} fruit days.`}
        eyebrow={south ? 'Southern hemisphere' : 'Moon planting calendar'}
        decorationEmoji="🌕"
      />

      <div className="container section">
        <HemisphereToggle
          current={hemisphere}
          to={moonPath(year, month, otherHemisphere(hemisphere))}
        />

        {/* Month at a glance */}
        <div className="moon-summary">
          {fullMoon && (
            <div className="moon-summary-item paper">
              <span className="moon-summary-emoji">🌕</span>
              <div>
                <p className="text-label" style={{ color: 'var(--sage)' }}>Full moon</p>
                <p className="text-title">{fullMoon.day} {monthName}</p>
              </div>
            </div>
          )}
          {newMoon && (
            <div className="moon-summary-item paper">
              <span className="moon-summary-emoji">🌑</span>
              <div>
                <p className="text-label" style={{ color: 'var(--sage)' }}>New moon</p>
                <p className="text-title">{newMoon.day} {monthName}</p>
              </div>
            </div>
          )}
          <div className="moon-summary-item paper">
            <span className="moon-summary-emoji">🌱</span>
            <div>
              <p className="text-label" style={{ color: 'var(--sage)' }}>Good sowing days</p>
              <p className="text-title">{bestSowing.length} days</p>
            </div>
          </div>
        </div>

        <p className="text-body moon-intro">
          The moon passes through all twelve signs of the zodiac each month, and biodynamic growers
          match what they sow to the element the moon is travelling through. In {monthName} {year} the
          moon is ascending{south && ' in southern skies'} — the traditional time for sowing and
          grafting — on {days.filter(d => d.ascending).length} days.{' '}
          {bestSowing.length > 0 && (
            <>The strongest sowing dates fall on the {bestSowing.slice(0, 3).map(d => d.day).join(', ')} and{' '}
            {bestSowing.length > 3 ? `${bestSowing.length - 3} other days` : 'a handful of others'}.</>
          )}{' '}
          Days marked in the table below sit near a lunar node or perigee, when traditional practice is
          to leave the soil alone.
        </p>

        {south && (
          <p className="hemisphere-note">
            <strong>Southern hemisphere.</strong> Moon phases and day types are the same the world
            over — a root day in Sussex is a root day in Canterbury. What changes is that ascending
            and descending are reversed, because the moon climbs southern skies as its declination
            moves south, and the sowing lists follow southern seasons.
          </p>
        )}

        {/* Day-by-day table */}
        <div className="moon-table-wrap">
          <table className="moon-table">
            <caption className="text-caption">
              Day-by-day moon planting calendar for {monthName} {year}
              {south && ' — southern hemisphere'}
            </caption>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Moon phase</th>
                <th scope="col">Sign</th>
                <th scope="col">Day type</th>
                <th scope="col">Sow or rest</th>
              </tr>
            </thead>
            <tbody>
              {days.map(d => (
                <tr key={d.day} className={d.avoid ? 'moon-row--avoid' : ''}>
                  <th scope="row" className="moon-cell-date">
                    <span className="moon-cell-daynum">{d.day}</span>
                    <span className="text-caption" style={{ color: 'var(--ink-faint)' }}>{d.weekday}</span>
                  </th>
                  <td>
                    {/* Mirrored below the equator, where a waxing moon is lit from the other side */}
                    <span
                      className={south ? 'moon-glyph moon-glyph--mirrored' : 'moon-glyph'}
                      aria-hidden="true"
                    >
                      {MOON_PHASE_EMOJI[d.phase]}
                    </span>{' '}
                    <span className="text-small">{PHASE_LABEL[d.phase]}</span>
                    <span className="text-caption moon-illum">{d.illumination}%</span>
                  </td>
                  <td className="text-small">
                    <span aria-hidden="true">{ZODIAC_SYMBOLS[d.sign]}</span>{' '}
                    {d.sign.charAt(0).toUpperCase() + d.sign.slice(1)}
                  </td>
                  <td>
                    <span className={`moon-daytype moon-daytype--${DAY_TYPE_META[d.dayType].color}`}>
                      {DAY_TYPE_META[d.dayType].emoji} {DAY_TYPE_META[d.dayType].label}
                    </span>
                  </td>
                  <td className="text-small">
                    {d.avoidReason
                      ? <span className="moon-avoid-note">{d.avoidReason}</span>
                      : d.ascending
                        ? 'Ascending — sow and graft'
                        : 'Descending — transplant and prune'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* What each day type suits, tied to the plant database */}
        <h2 className="text-headline moon-section-heading">What to sow in {monthName}</h2>
        {sowThisMonth.length > 0 ? (
          <div className="moon-daytype-grid">
            {dayTypeOrder.map(dt => {
              const plants = sowThisMonth.filter(p => p.biodynamicDay === dt);
              if (plants.length === 0) return null;
              return (
                <div key={dt} className={`moon-daytype-card paper moon-daytype-card--${DAY_TYPE_META[dt].color}`}>
                  <h3 className="text-title">
                    {DAY_TYPE_META[dt].emoji} {DAY_TYPE_META[dt].label}s
                    <span className="text-caption moon-daytype-count">
                      {counts[dt]} in {monthName}
                    </span>
                  </h3>
                  <p className="text-small" style={{ color: 'var(--ink-light)' }}>
                    {DAY_TYPE_META[dt].description}
                  </p>
                  <ul className="moon-plant-list">
                    {plants.map(p => (
                      <li key={p.id}>
                        <Link to={`/plants/${p.id}`}>{p.emoji} {p.name}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-body" style={{ color: 'var(--ink-light)' }}>
            Nothing in the plant database is sown in {monthName} — a month for planning, composting and
            tool maintenance rather than sowing.
          </p>
        )}

        {harvestThisMonth.length > 0 && (
          <>
            <h2 className="text-headline moon-section-heading">Ready to harvest in {monthName}</h2>
            <ul className="moon-harvest-list">
              {harvestThisMonth.map(p => (
                <li key={p.id}>
                  <Link to={`/plants/${p.id}`}>{p.emoji} {p.name}</Link>
                </li>
              ))}
            </ul>
          </>
        )}

        <p className="text-body moon-index-note">
          Want the reasoning behind all this? The{' '}
          <Link to="/biodynamic">biodynamic growing guide</Link>{' '}
          covers the day types, the eight preparations and where the practice came from.
        </p>

        {/* Prev / next keeps every month reachable by crawling */}
        <nav className="moon-pagination" aria-label="Other months">
          <Link to={moonPath(prev.getFullYear(), prev.getMonth(), hemisphere)}>
            ← {MONTH_NAMES[prev.getMonth()]} {prev.getFullYear()}
          </Link>
          <Link to={moonIndexPath(hemisphere)}>All months</Link>
          <Link to={moonPath(next.getFullYear(), next.getMonth(), hemisphere)}>
            {MONTH_NAMES[next.getMonth()]} {next.getFullYear()} →
          </Link>
        </nav>
      </div>
    </div>
  );
}
